import {
  CONTACT_FILTERS,
  type ContactFilterKey,
  FUNNEL_COLUMNS,
  formatDateTime,
  formatElapsed,
  type LeadStageKey,
} from '@crm/shared';
import { Badge, Button, cn, Select, Spinner } from '@crm/ui';
import { ChartNoAxesColumn, Megaphone, Send, Target } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  EmptyState,
  KanbanBoard,
  KanbanCard,
  KanbanColumn,
  PageHeader,
} from '@/components';
import { useDocumentTitle, useFullWidth, usePlainBackground } from '@/hooks';
import { useCampaign } from '@/modules/campaigns/api';
import { useUnits } from '@/modules/company/hooks';
import { CONVERSATIONS_ROUTES } from '@/modules/conversations/api';
import { LEADS_ROUTES, type Lead, useLeadBoard, useMoveLead } from '../api';
import { ManualMessageModal } from '../components/manual-message-modal';
import { MoveMenu } from '../components/move-menu';
import { ordenarLeads } from '../ordering';

/** A partir daqui o lead esta parado tempo demais e o tempo aparece em destaque. */
const PARADO_MS = 2 * 24 * 60 * 60 * 1000;

function LeadCard({
  lead,
  onOpen,
  onDragStart,
  onMove,
}: {
  lead: Lead;
  onOpen: () => void;
  onDragStart: () => void;
  onMove: (stage: LeadStageKey) => void;
}) {
  const parado = Date.now() - new Date(lead.enteredStageAt).getTime() > PARADO_MS;

  return (
    <KanbanCard draggable onDragStart={onDragStart}>
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-2 text-left">
        <Avatar name={lead.displayName} image={lead.customerImage} className="h-8 w-8 shrink-0" />
        <p className="min-w-0 flex-1 truncate text-sm font-medium">{lead.displayName}</p>
        {/* Ha quanto tempo parado nesta etapa: e o que separa o lead vivo do
            que ninguem tocou desde a semana passada. */}
        <span
          title={`Nesta etapa desde ${formatDateTime(lead.enteredStageAt)}`}
          className={cn(
            'shrink-0 text-xs tabular-nums',
            parado ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
          )}
        >
          {formatElapsed(lead.enteredStageAt)}
        </span>
      </button>

      <button type="button" onClick={onOpen} className="w-full text-left">
        {lead.lastMessage ? (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {/* Sem saber quem falou por ultimo, toda mensagem parece pendencia. */}
            <span className="font-medium text-foreground">
              {lead.lastMessageInbound ? 'Cliente: ' : 'Voce: '}
            </span>
            {lead.lastMessage}
          </p>
        ) : null}
      </button>

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap gap-1">
          {lead.waitingReply ? (
            <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
              Aguardando resposta
            </Badge>
          ) : null}
          {lead.ownerName ? <Badge variant="secondary">{lead.ownerName}</Badge> : null}
        </div>

        {/* No celular nao se arrasta card: mover tem de caber em um toque. */}
        <MoveMenu stage={lead.stage} leadName={lead.displayName} onMove={onMove} />
      </div>
    </KanbanCard>
  );
}

/** Funil de vendas: onde cada contato parou no caminho ate marcar horario. */
export function FunnelBoardPage() {
  useDocumentTitle('Funil de vendas');
  usePlainBackground();
  // O quadro cresce com a tela, como a agenda: quatro colunas nao cabem na
  // largura de leitura das telas de formulario.
  useFullWidth();

  const navigate = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  const { data: units } = useUnits();
  const [unitId, setUnitId] = useState('');
  const [modalAberta, setModalAberta] = useState(false);
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [contato, setContato] = useState<ContactFilterKey>('ALL');

  const campaignId = parametros.get('campaignId') ?? '';
  const campanha = useCampaign(campaignId);

  const { data, isLoading } = useLeadBoard({
    unitId: unitId || undefined,
    campaignId: campaignId || undefined,
  });
  const mover = useMoveLead();

  const leads = data?.data ?? [];
  const unidades = units?.data ?? [];

  function abrir(lead: Lead) {
    navigate(
      lead.conversationId
        ? `${CONVERSATIONS_ROUTES.LIST}?c=${lead.conversationId}`
        : CONVERSATIONS_ROUTES.LIST,
    );
  }

  function soltar(stage: LeadStageKey) {
    if (arrastando) mover.mutate({ id: arrastando, stage });
    setArrastando(null);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Funil de vendas"
        description="Do primeiro contato ao horario marcado, coluna a coluna."
        icon={<Target />}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(LEADS_ROUTES.CONVERSION)}>
              <ChartNoAxesColumn className="h-4 w-4" />
              <span>
                <span className="hidden sm:inline">Relatorios de </span>Conversao
              </span>
            </Button>
            <Button onClick={() => setModalAberta(true)}>
              <Send className="h-4 w-4" />
              <span>
                <span className="hidden sm:inline">Enviar </span>Mensagem
              </span>
            </Button>
          </>
        }
      />

      {/* Quadro filtrado nao pode parecer o quadro inteiro: quem chega de
          Resultados precisa ver de qual campanha sao estes leads. */}
      {campaignId ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-primary">
            <Megaphone className="h-3.5 w-3.5" />
            {campanha.data?.name ?? 'Campanha'}
          </span>
          <button
            type="button"
            onClick={() => setParametros({})}
            className="text-muted-foreground underline-offset-2 hover:underline"
          >
            Ver todos os leads
          </button>
        </div>
      ) : null}

      {unidades.length > 1 ? (
        <Select
          value={unitId}
          onChange={(event) => setUnitId(event.target.value)}
          className="block w-auto max-w-xs border-transparent bg-transparent px-1 text-muted-foreground hover:bg-muted/60"
        >
          <option value="">Todas as unidades</option>
          {unidades.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </Select>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : leads.length === 0 ? (
        <EmptyState
          icon={<Target />}
          title={campaignId ? 'Nenhum lead desta campanha ainda' : 'Nenhum lead no funil ainda'}
        />
      ) : (
        <KanbanBoard>
          {FUNNEL_COLUMNS.map((coluna) => {
            const etapas: readonly LeadStageKey[] =
              coluna.key === 'CONTACT' && contato !== 'ALL' ? [contato] : coluna.stages;
            const doEstagio = ordenarLeads(leads.filter((lead) => etapas.includes(lead.stage)));
            // Soltar na coluna de contato precisa de um destino concreto: o
            // filtro escolhido manda, e com "todos" fica o atendimento humano.
            const destino: LeadStageKey =
              coluna.key === 'CONTACT' && contato === 'ALL'
                ? 'HUMAN_CONTACT'
                : (etapas[0] ?? 'LEAD');

            return (
              <KanbanColumn
                key={coluna.key}
                label={coluna.label}
                hint={coluna.hint}
                count={doEstagio.length}
                highlighted={Boolean(arrastando)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => soltar(destino)}
                emptyLabel="Ninguem nesta etapa"
                extra={
                  coluna.key === 'CONTACT' ? (
                    <div className="mt-2 flex gap-1">
                      {CONTACT_FILTERS.map((filtro) => (
                        <button
                          key={filtro.key}
                          type="button"
                          onClick={() => setContato(filtro.key)}
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
                            contato === filtro.key
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:text-foreground',
                          )}
                        >
                          {filtro.label}
                        </button>
                      ))}
                    </div>
                  ) : null
                }
              >
                {doEstagio.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onOpen={() => abrir(lead)}
                    onDragStart={() => setArrastando(lead.id)}
                    onMove={(destino) => mover.mutate({ id: lead.id, stage: destino })}
                  />
                ))}
              </KanbanColumn>
            );
          })}
        </KanbanBoard>
      )}

      {modalAberta ? <ManualMessageModal onClose={() => setModalAberta(false)} /> : null}
    </div>
  );
}
